import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://superbuy.sbs';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = 'website',
  jsonLd,
  noIndex = false,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (attrName: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el: HTMLMetaElement | null = document.querySelector(`meta[${attr}="${attrName}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, attrName);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:card', 'summary_large_image');

    if (ogImage) {
      setMeta('og:image', ogImage, true);
      setMeta('twitter:image', ogImage);
    }

    const canonicalFull = canonicalPath ? `${siteUrl}${canonicalPath}` : siteUrl;
    setMeta('og:url', canonicalFull, true);

    let canonLink: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    if (!canonLink) {
      canonLink = document.createElement('link');
      canonLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonLink);
    }
    canonLink.setAttribute('href', canonicalFull);

    if (noIndex) {
      setMeta('robots', 'noindex, follow');
    } else {
      setMeta('robots', 'index, follow');
    }

    document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach((el) => el.remove());

    if (jsonLd) {
      const ldArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      ldArray.forEach((ld) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'true');
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[type="application/ld+json"][data-seo="true"]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonicalPath, ogImage, ogType, jsonLd, noIndex]);

  return null;
}